import {
    ChangeDetectorRef, Component, Inject, OnInit, AfterViewInit, OnChanges, OnDestroy,
    SimpleChanges, Optional, Host, HostListener, Input, Output, EventEmitter, ElementRef,
    Renderer2, ViewChild, NgZone, DOCUMENT
} from "@angular/core";
import { PanchangCalendarComponent } from "../panchang-calendar.component";
import { PanchangService } from '@otas/utils/custom-components/panchang-calendar/panchang.service';
import { AppModalService } from '@otas/shared/ui/app-modal/app-modal.service';
import { NgbActiveModal } from '@otas/modal/modal-ref';
import { PanchangPopupHelperService } from 'app/services/panchang-popup-helper.service';
import { DateService } from 'app/services/date.service';
import { EnterKeyListenerDirective } from '@otas/utils/directives/helper/enter.directive';
import { bookingNow, contextMinBookingDate } from '@otas/shared/booking-day-policy';
import { istDayKey, istEpoch, istFormatTime, istParts, istStartOfDay } from '@otas/utils/custom-components/panchang-calendar/ist';
import { ContinuosBookingModalComponent } from "@otas/pooja-shared/continuos-booking-modal/continuos-booking-modal.component";
import { ContinuosBookingModalService } from "@otas/pooja-shared/continuos-booking-modal/continuos-booking-modal.service";
import { PuchangamModalService } from "./panchangam-modal.service";
import { EN_MONTHS, ML_MONTHS, NAKSATRAS, TITHI, WEEKDAYS } from 'app/app.constants';
import dayjs from 'dayjs';
import * as _ from 'underscore';
import { first } from 'rxjs/operators';

/** One scrollable month block of the Google-scheduler-style calendar. */
interface PcvMonth {
    /** year * 12 + month — stable identity + ordering key. */
    key: number;
    year: number;
    /** 0-based English month. */
    month: number;
    /** Sticky header line, e.g. "2026 November" (year rolls with the month). */
    label: string;
    /** English month name alone (the sticky header's clickable month chip). */
    monthName: string;
    /** Malayalam era line, e.g. "1202 · Thulam-Vrischikam". */
    mlLabel: string;
    /** Rows of 7 day cells straight from PanchangService.calculateWeeks. */
    weeks: any[][];
    /** The same cells flattened in chronological order — the mobile list view
     *  and the filtered row view render from this. */
    days: any[];
}

/** In-panel continuous-booking filter values ('' = dimension unused). `week`
 *  is the 0-based weekday index; `month` the 0-based English month or 'E1'
 *  (= the 1st of every English month); `mlMonth` 'ml<i>' into ML_MONTHS or
 *  'M1' (= the 1st of every Malayalam month) — all strings (native-select
 *  friendly); star/thithi hold the picked name. */
interface PcvFilters {
    star: string;
    thithi: string;
    week: string;
    month: string;
    mlMonth: string;
    /** Pradosham days only (owner round 14) — same source of truth as the
     *  legacy Continuous Booking checkbox: rows of the shared panchang table
     *  carrying a truthy `pradosham` field. */
    pradosham: boolean;
}

/**
 * Redesigned panchang calendar (2026-07-16): one vertically scrolling list of
 * month grids (Google-Calendar schedule style) instead of the single-month
 * table with prev/next arrows. Every month's "2026 November" header is
 * position:sticky inside the scroller, so the current month (and its year —
 * it rolls at December automatically) stays pinned while its days scroll by.
 * Months are built LAZILY on scroll — panchang math is untouched, each block
 * still comes from PanchangService.calculateWeeks (the drik-verified engine).
 *
 * Selection: single tap by default (the legacy choosedate contract). The
 * "Multiple Date" button switches to in-calendar multi-select with a
 * full-width Submit bar at the bottom; the fa-filter button next to it opens
 * the Continuous Booking modal (star/thithi/week/pradosham suggestions) —
 * the legacy Multiple Date flow. Both emit the same
 * panchangModalEvent {result:'continous', date:[epoch...]} payload, so every
 * caller (counter, glass portal, booking-v2) works unchanged.
 */
@Component({
    selector: 'app-panchang-calendar-view',
    templateUrl: './panchang-calendar-view.component.html',
    styleUrls: ['./panchang-calendar-view.component.scss'],
    standalone: false
})
export class PanchangCalendarViewComponent
    extends PanchangCalendarComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    @Input() holidays: any[] = [];
    @Input() pooja: any;
    @Input() title: string;
    @Input() keyboard: 'english' | 'malayalam' | 'manglish';
    @Output() closePanchang = new EventEmitter<void>();

    // NOTE: not named `months` — the base input component already has a
    // `get months()` accessor (the legacy month dropdown source).
    monthBlocks: PcvMonth[] = [];
    multiMode = false;
    appending = false;
    /** Phone layout (≤640px): full-height modal, day cells as full-width
     *  list rows instead of the 7-column grid. */
    isMobile = false;
    /** After the first "Earlier month" click, scrolling near the top keeps
     *  loading previous months automatically (the button disappears). */
    earlierUnlocked = false;
    /** In-panel continuous-booking filter (owner round 5). */
    filterOpen = false;
    /** Mobile picks ONE dimension at a time; desktop shows all dropdowns. */
    filterDimension: '' | 'star' | 'thithi' | 'week' | 'month' | 'mlMonth' = '';
    filters: PcvFilters = { star: '', thithi: '', week: '', month: '', mlMonth: '', pradosham: false };
    /** Weekday OCCURRENCE sub-filter (owner round 12, the legacy modal's week
     *  sub-select): '' = every picked weekday; 'e1'..'e4'/'m1'..'m4' = the
     *  nth weekday of the English/Malayalam month; 'e5'/'m5' = the LAST one. */
    weekOption = '';
    /** Period the filter spans (IST day keys, inclusive) — a month-granularity
     *  period app-date-picker like the advanced continuous window; lets a
     *  star/thithi filter cover 1–3 years. Opening the panel presets ONE YEAR
     *  from the current month; only a USER-picked range activates filtering by
     *  itself (`filterRangeTouched`), the default is just the scope the
     *  dimension filters select over. */
    filterRange: { startKey: number; endKey: number } | null = null;
    filterRangeTouched = false;
    // STABLE dayjs instances for the picker's [rangeStart]/[rangeEnd] — a
    // fresh-object getter here would re-trigger its ngOnChanges every CD pass
    // (the NG0103 option-list lesson, same family).
    filterRangeStartDj: dayjs.Dayjs | null = null;
    filterRangeEndDj: dayjs.Dayjs | null = null;
    /** Day the list view scrolls to after a build (mobile opens at TODAY /
     *  the anchor day instead of the month start). */
    private pendingScrollDayKey: number | null = null;
    /** Malayalam era of the month currently under the sticky header. */
    visibleMlLabel = '';
    /** Month key (year*12+month) currently under the sticky header — drives
     *  the decorated toolbar (month + centered year) and the Today button. */
    visibleKey: number | null = null;

    @ViewChild('scrollHost') scrollHost: ElementRef<HTMLElement>;

    private multiSelected = new Map<number, { year: number; month: number; date: number }>();
    private fetchedBookedMonths = new Set<number>();
    private viewReady = false;
    private initSeq = 0;
    private removeScrollListener: (() => void) | null = null;
    private mobileQuery: MediaQueryList | null = null;
    private removeMobileListener: (() => void) | null = null;
    private cellSizer: ResizeObserver | null = null;
    /** Marking a DESTROYED view dirty from a late timer/promise loops change
     *  detection forever (the view can never be refreshed clean — NG0103).
     *  Every async continuation checks this before touching the cdr. */
    private destroyed = false;

    /** Kollavarsham calculator ceiling (mirrors the continuous modal's
     *  2100-12-31 picker max) — the scroller never appends past it. */
    private static readonly MAX_MONTH_KEY = 2100 * 12 + 11;

    /** Day-cell geometry. The floor used to be 100px "so the star/tithi lines
     *  stay readable", but a floor TALLER than the space available is exactly
     *  what clipped the last week row: 5 rows + 84px of month bars need a
     *  584px scroller, which no viewport under ~780px tall has (owner 2026-08:
     *  a laptop showed four and a half rows and never the next golden bar).
     *  The cell may now shrink to what actually fits and --pcv-scale shrinks
     *  its typography with it — THAT is what keeps the lines readable. */
    private static readonly MIN_CELL_H = 76;
    private static readonly MAX_CELL_H = 140;
    /** cell height at which the cell's own type renders at its full size */
    private static readonly DESIGN_CELL_H = 116;
    /** below this the type stops shrinking (it would stop being legible) */
    private static readonly MIN_SCALE = 0.72;

    constructor(
        @Inject(DOCUMENT) document: any,
        renderer: Renderer2,
        elementRef: ElementRef,
        @Optional() @Host() enterDirective: EnterKeyListenerDirective,
        appModal: AppModalService,
        popOverService: PanchangPopupHelperService,
        ps: PanchangService,
        private zone: NgZone,
        private cdr: ChangeDetectorRef,
        @Optional() private activeModal: NgbActiveModal,
        @Optional() private punchangamModalService: PuchangamModalService,
        @Optional() private continuosBookingModalService: ContinuosBookingModalService
    ) {
        super(document, renderer, elementRef, enterDirective, appModal, popOverService, ps);
    }

    override ngOnInit() {
        super.ngOnInit();
        if (!this.currentDate) {
            this.currentDate = new Date();
        }
        if (!this.chosenDate) {
            this.chosenDate = new Date();
        }
        this.lang = sessionStorage.getItem('language');
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        // Rebuild the scroll model when the anchor/options change after init.
        if (this.viewReady && (changes['currentDate'] || changes['options'])) {
            this.initScrollCalendar();
        }
    }

    override ngAfterViewInit() {
        // Don't call super.ngAfterViewInit() — it touches the inline screen's
        // inputField, which doesn't exist in this view (legacy behavior).
        this.viewReady = true;
        this.bindMobileQuery();
        this.initScrollCalendar();
        this.bindScrollListener();
        this.bindCellSizer();
    }

    override ngOnDestroy() {
        this.destroyed = true;
        this.initSeq++; // cancels every pending build loop / late continuation
        if (this.removeScrollListener) {
            this.removeScrollListener();
            this.removeScrollListener = null;
        }
        if (this.removeMobileListener) {
            this.removeMobileListener();
            this.removeMobileListener = null;
        }
        if (this.cellSizer) {
            this.cellSizer.disconnect();
            this.cellSizer = null;
        }
        super.ngOnDestroy();
    }

    /** Phone breakpoint tracking (list layout + full-height modal). */
    private bindMobileQuery(): void {
        if (!window.matchMedia) {
            return;
        }
        this.mobileQuery = window.matchMedia('(max-width: 640px)');
        this.isMobile = this.mobileQuery.matches;
        const onChange = (e: MediaQueryListEvent) => this.zone.run(() => {
            this.isMobile = e.matches;
            this.cdr.markForCheck();
        });
        this.mobileQuery.addEventListener('change', onChange);
        this.removeMobileListener = () => this.mobileQuery && this.mobileQuery.removeEventListener('change', onChange);
    }

    /** Derives the day-cell height from the scroller's MEASURED height so one
     *  month (sticky header + 5 week rows) plus the NEXT month's golden bar
     *  always fit the view — whatever chrome is open (filter bar, multi
     *  footer, short portal viewports). Replaces the old vh-guess formula,
     *  which clipped the 5th row whenever extra chrome appeared (owner round
     *  7 items 2/3/5). Pure DOM CSS-var plumbing — no change detection. */
    private bindCellSizer(): void {
        const host = this.scrollHost && this.scrollHost.nativeElement;
        const root: HTMLElement = this._elementRef && this._elementRef.nativeElement;
        if (!host || !root) {
            return;
        }
        const apply = () => {
            const h = host.clientHeight;
            if (!h) {
                return;
            }
            // ~42px sticky month header + ~42px of the next month's golden bar
            // share the viewport with the 5 week rows
            const cell = Math.max(PanchangCalendarViewComponent.MIN_CELL_H,
                Math.min(PanchangCalendarViewComponent.MAX_CELL_H, Math.floor((h - 84) / 5)));
            root.style.setProperty('--pcv-cell-h', cell + 'px');
            // …and the cell's own type shrinks with it, so a short screen
            // scales the whole calendar down instead of clipping a week row
            const scale = Math.max(PanchangCalendarViewComponent.MIN_SCALE,
                Math.min(1, cell / PanchangCalendarViewComponent.DESIGN_CELL_H));
            root.style.setProperty('--pcv-scale', scale.toFixed(3));
        };
        if (typeof ResizeObserver !== 'undefined') {
            this.zone.runOutsideAngular(() => {
                this.cellSizer = new ResizeObserver(apply);
                this.cellSizer.observe(host);
            });
        }
        apply();
    }

    // ---- scroll model -----------------------------------------------------

    /** (Re)builds the month list around the chosen/anchor date. */
    async initScrollCalendar() {
        this.fetchedBookedMonths.clear();
        this.multiSelected.clear();
        const anchor = this.currentDate || new Date(istStartOfDay(bookingNow()));
        // The list view (mobile) lands on the anchor DAY, not the month start.
        this.pendingScrollDayKey =
            anchor.getFullYear() * 10000 + (anchor.getMonth() + 1) * 100 + anchor.getDate();
        await this.buildWindow(anchor.getFullYear() * 12 + anchor.getMonth());
    }

    /** Sticky-header year/month jump (the legacy dropdown navigation):
     *  rebuilds the scroller at the picked month (policy-clamped). In FILTER
     *  view the list is ONE continuous window over the scoped range — a jump
     *  extends it to cover the target and scrolls there, NEVER rebuilds: the
     *  rebuild left a detached 2-month island (Current-Month wiped the rows;
     *  a far jump showed an empty list) while the ticked days lived on
     *  invisibly. */
    async jumpToMonth(year: number, month: number) {
        this.headEdit = null;
        const key = year * 12 + month;
        if (this.filtersActive) {
            const seq = this.initSeq;
            await this.extendWindowForFilter(key);
            // a tick later: the last appended month's section must be in the
            // DOM before we can land on it
            setTimeout(() => seq === this.initSeq && this.scrollToMonthKey(key));
            return;
        }
        await this.buildWindow(key);
    }

    /** Builds the window [current month (clamped to the policy minimum) ..
     *  target + 1] and scrolls to the target — so a future chosen date OR a
     *  jump back to the current year never hides today's month behind
     *  repeated "Earlier month" clicks. A far-future target (>1 year ahead)
     *  renders alone; the jump panels cover the gap back. */
    private async buildWindow(targetKey: number): Promise<void> {
        const seq = ++this.initSeq;
        // Hop off the caller's tick FIRST: buildWindow is (also) called from
        // ngAfterViewInit, and mutating template-bound state synchronously
        // inside a change-detection hook throws NG0100 in dev mode.
        await Promise.resolve();
        if (seq !== this.initSeq) {
            return;
        }
        this.monthBlocks = [];
        this.hideTip();
        this.earlierUnlocked = false;
        this.cdr.markForCheck();
        const min = contextMinBookingDate();
        const minKey = min ? this.minMonthKey(min) : null;
        const nowP = istParts(bookingNow());
        let startKey = Math.min(targetKey, nowP.year * 12 + nowP.month);
        if (minKey !== null && startKey < minKey) {
            startKey = minKey;
        }
        if (targetKey < startKey) {
            targetKey = startKey;
        }
        // an open filter panel's period bounds jumps too (same rule as the
        // scroll appends) — never below the window start, so a degenerate
        // inverted range can't empty the calendar
        const ceiling = Math.max(this.appendCeilingKey, startKey);
        if (targetKey > ceiling) {
            targetKey = ceiling;
        }
        if (targetKey - startKey > 11) {
            startKey = targetKey;
        }
        for (let key = startKey; key <= Math.min(targetKey + 1, ceiling) && seq === this.initSeq; key++) {
            await this.addMonth(Math.floor(key / 12), key % 12, 'end', seq);
            if (key === targetKey) {
                // land on the target as soon as it exists — the rest of the
                // window keeps filling below without holding the view hostage
                this.updateVisibleLabel();
                setTimeout(() => seq === this.initSeq && this.scrollToMonthKey(targetKey));
            }
            // yield a macrotask between month builds: the kollavarsham pass is
            // CPU-heavy, and without a paint break a long window (jump-backs)
            // froze scrolling for seconds
            await new Promise(resolve => setTimeout(resolve));
        }
        if (seq !== this.initSeq) {
            return;
        }
        this.updateVisibleLabel();
    }

    // ---- sticky-header year/month jump panels (app-date-picker style) ------

    headEdit: { key: number; part: 'year' | 'month'; x: number; y: number } | null = null;
    /** First year of the 12-year page shown in the year panel. */
    yearPage = 1950;

    /** Years offered for jumping — years entirely before the booking-day
     *  policy minimum are excluded (their months are all disabled). Runs to
     *  2100 (the scroller's ceiling): calculateWeeks computes every cell with
     *  the kollavarsham calculator, so months after panchang.json's 2050 tail
     *  work natively. */
    get jumpYears(): number[] {
        const min = contextMinBookingDate();
        const minYear = min ? istParts(min).year : 1950;
        const years: number[] = [];
        for (let y = Math.max(1950, minYear); y <= 2100; y++) {
            years.push(y);
        }
        return years;
    }

    /** The month block the open jump panel belongs to. */
    get headEditBlock(): PcvMonth | null {
        if (!this.headEdit) {
            return null;
        }
        const key = this.headEdit.key;
        return this.monthBlocks.find(b => b.key === key) || null;
    }

    get yearPageOptions(): number[] {
        return this.jumpYears.filter(y => y >= this.yearPage && y < this.yearPage + 12);
    }

    get yearPageEnd(): number {
        return this.yearPage + 11;
    }

    get canYearPagePrev(): boolean {
        return this.jumpYears.some(y => y < this.yearPage);
    }

    get canYearPageNext(): boolean {
        return this.jumpYears.some(y => y >= this.yearPage + 12);
    }

    yearPagePrev(): void {
        if (this.canYearPagePrev) {
            this.yearPage -= 12;
        }
    }

    yearPageNext(): void {
        if (this.canYearPageNext) {
            this.yearPage += 12;
        }
    }

    /** A month is disabled in the jump panel when it lies entirely before the
     *  policy minimum's month. */
    isMonthDisabled(year: number, month: number): boolean {
        const min = contextMinBookingDate();
        return !!min && year * 12 + month < this.minMonthKey(min);
    }

    isHeadEdit(m: PcvMonth, part: 'year' | 'month'): boolean {
        return !!this.headEdit && this.headEdit.key === m.key && this.headEdit.part === part;
    }

    openHeadEdit(m: PcvMonth, part: 'year' | 'month', event: Event): void {
        event.stopPropagation();
        // The panel renders at the component ROOT, absolute-positioned at the
        // clicked chip. Coordinates are host-relative on purpose: the AppModal
        // pane is centered with transform:translate(-50%,-50%), and a
        // transformed ancestor re-bases position:fixed descendants — fixed
        // viewport coords landed the panel far away from the chip.
        const target = event && (event.currentTarget as HTMLElement);
        const rect = target && target.getBoundingClientRect
            ? target.getBoundingClientRect() : ({ left: 20, top: 100, bottom: 130 } as DOMRect);
        const host = this.hostRect();
        const panelWidth = 268;
        const panelHeight = 250;
        const x = Math.max(8, Math.min(rect.left - host.left, host.width - panelWidth - 8));
        let y = rect.bottom - host.top + 8;
        if (y + panelHeight > host.height - 8) {
            y = Math.max(8, rect.top - host.top - panelHeight - 8);
        }
        this.headEdit = { key: m.key, part, x, y };
        if (part === 'year') {
            const first = this.jumpYears.length ? this.jumpYears[0] : 1950;
            this.yearPage = first + Math.floor((Math.max(m.year, first) - first) / 12) * 12;
        }
    }

    onHeadYearChange(m: PcvMonth, value: any): void {
        this.headEdit = null;
        const year = +value;
        if (year) {
            this.jumpToMonth(year, m.month);
        }
    }

    onHeadMonthChange(m: PcvMonth, value: any): void {
        this.headEdit = null;
        const month = +value;
        if (!isNaN(month)) {
            this.jumpToMonth(m.year, month);
        }
    }

    @HostListener('document:click')
    closeJumpPanels(): void {
        if (this.headEdit) {
            this.headEdit = null;
        }
    }

    // ---- booked-devotee floating tooltip (outside the cell — the details
    // list many devotees/stars and must not be clipped by the cell) ---------

    tip: { title: string; content: any; x: number; y: number; side: 'left' | 'right'; arrowY: number } | null = null;

    showTip(day: any, event: MouseEvent): void {
        if (!day || !day.content) {
            this.hideTip();
            return;
        }
        // Anchor the card RIGHT BESIDE the hovered cell, vertically centred
        // on it, with the arrow pointing at the cell's edge. Host-relative
        // coordinates (see openHeadEdit — the transformed modal pane re-bases
        // fixed positioning, which pushed the card far from the cell).
        const cell = (event.currentTarget as HTMLElement).getBoundingClientRect();
        const host = this.hostRect();
        const rect = {
            left: cell.left - host.left, right: cell.right - host.left,
            top: cell.top - host.top, height: cell.height,
        };
        const width = 280;
        const maxHeight = 270;
        let side: 'left' | 'right' = 'right';
        let x = rect.right + 12;
        if (x + width > host.width - 8) {
            side = 'left';
            x = Math.max(8, rect.left - width - 12);
        }
        const cellMid = rect.top + rect.height / 2;
        const y = Math.max(8, Math.min(cellMid - 60, host.height - maxHeight - 8));
        const arrowY = Math.max(12, Math.min(cellMid - y - 7, maxHeight - 26));
        this.tip = { title: day.title, content: day.content, x, y, side, arrowY };
    }

    /** The component root's viewport rect — the coordinate base for the
     *  absolutely-positioned jump panels and devotee tooltip. */
    private hostRect(): DOMRect {
        const el: HTMLElement = this._elementRef && this._elementRef.nativeElement;
        return el && el.getBoundingClientRect
            ? el.getBoundingClientRect()
            : ({ left: 0, top: 0, width: window.innerWidth, height: window.innerHeight } as DOMRect);
    }

    hideTip(): void {
        if (this.tip) {
            this.tip = null;
        }
    }

    private minMonthKey(min: Date): number {
        // The policy minimum is an IST-midnight instant — decompose it on the
        // IST clock (a host-local getMonth() reads the previous day abroad).
        const p = istParts(min);
        return p.year * 12 + p.month;
    }

    /** While the filter panel is open, its period (the DEFAULT one included)
     *  bounds the scroller: months past the range's end month never build —
     *  the calendar ends where the "to" month says (owner round 11: the
     *  default "to Jul 2027" still let August 2027 scroll in; a picked end
     *  month behaved). Closed panel = the kollavarsham ceiling. */
    private get appendCeilingKey(): number {
        if (this.filterOpen && this.filterRange) {
            const endKey = this.filterRange.endKey;
            return Math.floor(endKey / 10000) * 12 + (Math.floor(endKey / 100) % 100) - 1;
        }
        return PanchangCalendarViewComponent.MAX_MONTH_KEY;
    }

    private async appendNext(seq = this.initSeq): Promise<void> {
        if (!this.monthBlocks.length) {
            return;
        }
        const last = this.monthBlocks[this.monthBlocks.length - 1];
        if (last.key >= this.appendCeilingKey) {
            return;
        }
        const year = last.month === 11 ? last.year + 1 : last.year;
        const month = (last.month + 1) % 12;
        await this.addMonth(year, month, 'end', seq);
    }

    /** True when the month before the first rendered one is still bookable
     *  (policy past-window / PAST), so "earlier" may be loaded. */
    get canLoadEarlier(): boolean {
        if (!this.monthBlocks.length) {
            return false;
        }
        const first = this.monthBlocks[0];
        if (first.year <= 1950) {
            return false;
        }
        const min = contextMinBookingDate();
        return !min || first.key - 1 >= this.minMonthKey(min);
    }

    /** "Earlier month" button: prepends the previous month, smooth-scrolls up
     *  to it, and UNLOCKS top-edge auto-loading — from here on, scrolling
     *  near the top keeps prepending previous months (position-preserving),
     *  so going back in time is one continuous scroll, not a click per month. */
    async loadEarlier() {
        if (this.appending || !this.canLoadEarlier) {
            return;
        }
        this.earlierUnlocked = true;
        await this.prependEarlier(true);
    }

    /** Scroll-handler variant: silent position-keeping prepend. */
    async loadEarlierAuto() {
        if (this.appending || !this.canLoadEarlier) {
            return;
        }
        await this.prependEarlier(false);
    }

    private async prependEarlier(scrollToNew: boolean): Promise<void> {
        this.appending = true;
        try {
            const first = this.monthBlocks[0];
            const year = first.month === 0 ? first.year - 1 : first.year;
            const month = (first.month + 11) % 12;
            const key = year * 12 + month;
            await this.addMonth(year, month, 'start', this.initSeq);
            if (scrollToNew) {
                // after the position-keeping compensation lands, glide up to
                // the newly-revealed month
                setTimeout(() => {
                    const host = this.scrollHost && this.scrollHost.nativeElement;
                    const section = host && host.querySelector<HTMLElement>('.pcv-month[data-key="' + key + '"]');
                    if (host && section) {
                        if (typeof host.scrollTo === 'function') {
                            host.scrollTo({ top: section.offsetTop, behavior: 'smooth' });
                        } else {
                            host.scrollTop = section.offsetTop;
                        }
                    }
                }, 30);
            }
        } finally {
            this.appending = false;
            if (!this.destroyed) {
                this.cdr.markForCheck();
            }
        }
    }

    async loadMore() {
        if (this.appending) {
            return;
        }
        this.appending = true;
        try {
            await this.appendNext();
        } finally {
            this.appending = false;
            if (!this.destroyed) {
                this.cdr.markForCheck();
            }
        }
    }

    private async addMonth(year: number, month: number, position: 'start' | 'end', seq: number): Promise<void> {
        if (seq !== this.initSeq) {
            return;
        }
        // ONE block per month, ever: the initial buildWindow loop and a
        // scroll-triggered append can race onto the SAME month (the
        // programmatic landing scroll fires a scroll event while the window
        // is still short) — a duplicate block rendered its days twice and
        // select-all then toggled the copy straight back OFF (owner round
        // 11: 12-Aug-2026 listed twice in the filtered list).
        if (this.monthBlocks.some(b => b.key === year * 12 + month)) {
            return;
        }
        const block = this.buildMonth(year, month);
        // Availability is fetched in the BACKGROUND and painted onto the month
        // when it lands — awaiting the per-month HTTP round trip here froze
        // scrolling for seconds on the counter (owner round 5).
        this.ensureBookedDates(year, month).then(() => {
            if (seq === this.initSeq) {
                this.redecorateBlock(block);
            }
        });
        if (position === 'end') {
            this.monthBlocks = [...this.monthBlocks, block];
        } else {
            const host = this.scrollHost && this.scrollHost.nativeElement;
            const heightBefore = host ? host.scrollHeight : 0;
            this.monthBlocks = [block, ...this.monthBlocks];
            // keep the reading position after the block renders above it
            setTimeout(() => {
                if (host) {
                    host.scrollTop += host.scrollHeight - heightBefore;
                }
            });
        }
        // monthBlocks changes land from async continuations — mark the view
        // dirty or Angular's targeted change detection never re-runs the
        // template pass (dev mode then throws NG0100 from checkNoChanges).
        this.cdr.markForCheck();
    }

    /** Availability fetch per month (the legacy nextMonth/prevMonth contract:
     *  poojaBookingComp.getBookedDates(monthEpoch) fills popOverService).
     *  Modal openers (counter/glass) pre-load popOverService instead — no
     *  poojaBookingComp there, exactly like the legacy arrows. */
    private async ensureBookedDates(year: number, month: number): Promise<void> {
        if (!this.poojaBookingComp) {
            return;
        }
        const key = year * 12 + month;
        if (this.fetchedBookedMonths.has(key)) {
            return;
        }
        this.fetchedBookedMonths.add(key);
        try {
            await this.poojaBookingComp.getBookedDates(new Date(year, month, 1).getTime() as any);
        } catch {
            // an availability fetch failure must not kill the calendar
        }
    }

    /** Builds + decorates one month block. The day-cell decoration is the
     *  legacy calculateWeeks() pass (names, IST bracket times, booking-day
     *  blocking, booked-day popover content) applied per month. */
    private buildMonth(year: number, month: number): PcvMonth {
        this.lang = sessionStorage.getItem('language');
        const weeks: any[][] = this.ps.calculateWeeks(month, year, true);
        const minBookable = contextMinBookingDate();
        const minKey = minBookable ? istDayKey(minBookable) : null;
        weeks.forEach(week => week.forEach(day => this.decorateDay(day, month, minKey)));

        // Malayalam-era header line in CHRONOLOGICAL day order (the >5-week
        // fold puts trailing days into row 0, so iterate by date instead).
        const days = _.sortBy(_.filter(_.flatten(weeks), (d: any) => !!d && !!d.date), (d: any) => d.date);
        const mlYears = _.uniq(_.map(days, (d: any) => d.mlDetails.mlyear));
        const mlMonths = _.uniq(_.map(days, (d: any) =>
            this.lang === 'malayalam' ? d.mlDetails.mlMasaName : d.mlDetails.masaName));
        const mlLabel = (mlYears.length > 1 ? mlYears[0] + '-' + mlYears[1] : mlYears[0])
            + ' · ' + mlMonths.join('-');

        const monthDef = this.ps.getMonth(month);
        const monthName = (monthDef && monthDef.en) || '';
        return {
            key: year * 12 + month, year, month, monthName,
            label: year + ' ' + monthName,
            mlLabel, weeks, days,
        };
    }

    /** Re-runs the day decoration for one block — used when its background
     *  booked-dates fetch lands (popOverService content arrives late). */
    private redecorateBlock(block: PcvMonth): void {
        if (this.destroyed) {
            return;
        }
        const minBookable = contextMinBookingDate();
        const minKey = minBookable ? istDayKey(minBookable) : null;
        block.weeks.forEach(week => week.forEach(day => this.decorateDay(day, block.month, minKey)));
        this.cdr.markForCheck();
    }

    private decorateDay(day: any, month: number, minKey: number | null): void {
        if (!day || !day.date) {
            return;
        }
        day.isSelected = !!this.chosenDate &&
            this.chosenDate.getDate() === day.date &&
            this.chosenDate.getFullYear() === day.year && this.chosenDate.getMonth() === day.month;

        // Booking-day policy (IST day keys — same rule as the legacy pass).
        const cellDayKey = day.year * 10000 + (month + 1) * 100 + day.date;
        if (minKey && cellDayKey < minKey) {
            day.blocked = true;
            day.isSelected = false;
        }
        day.isMultiSelected = this.multiSelected.has(cellDayKey);

        const nd: Date = day.mlDetails.naksatra.end;
        const td: Date = day.mlDetails.tithi.end;
        day.mlDetails.starName = this.lang === 'malayalam' ? day.mlDetails.naksatra.mlMalayalam : day.mlDetails.naksatra.enMalayalam;
        if (!!day.mlDetails.secondStar) {
            const secondStarEndTime: Date = day.mlDetails.secondStar.end;
            day.mlDetails.secondstarName =
                this.lang === 'malayalam' ? day.mlDetails.secondStar.mlMalayalam : day.mlDetails.secondStar.enMalayalam;
            day.mlDetails.secondStar.startAndend = istFormatTime(secondStarEndTime);
        }
        if (!!day.mlDetails.secondTithi) {
            const secondTithiEndTime: Date = day.mlDetails.secondTithi.end;
            day.mlDetails.secondTithiName =
                this.lang === 'malayalam' ? day.mlDetails.secondTithi.mlMalayalam : day.mlDetails.secondTithi.enMalayalam;
            day.mlDetails.secondTithi.startAndend = istFormatTime(secondTithiEndTime);
        }
        day.mlDetails.tithiName = this.lang === 'malayalam' ? day.mlDetails.tithi.mlMalayalam : day.mlDetails.tithi.enMalayalam;
        // Bracket times on the IST wall clock (never host-local).
        day.mlDetails.naksatra.startAndend = istFormatTime(nd);
        day.mlDetails.tithi.startAndend = istFormatTime(td);
        // Printed-calendar moon markers: Amavasi = dark new-moon disc,
        // Pournami = glowing full-moon disc (top-left of the cell).
        day.moon = PanchangCalendarViewComponent.moonPhaseOf(day.mlDetails);
        this.popOverService.content.forEach((c) => {
            if (DateService.isSame(c.date, new Date(day.year, day.month, day.date))) {
                day.content = c.content;
                day.title = c.title;
                day.isPartial = c.isPartial;
                // R19: warn-only poojas mark their red (limit-reached) days
                // overbookable — the cell stays clickable so the R18 "Book anyway?"
                // confirmation can run on add (an admin-blocked day never sets it).
                day.overbookable = c.overbookable;
            }
        });
    }

    /** Detects the tithi label of a day being Amavasi (new moon) or
     *  Pournami (full moon) in either language (second tithi included). */
    private static moonPhaseOf(ml: any): 'new' | 'full' | null {
        const label = (
            ((ml.tithi && (ml.tithi.enMalayalam || '')) + ' ' + (ml.tithi && (ml.tithi.mlMalayalam || ''))) + ' ' +
            (ml.secondTithi ? ((ml.secondTithi.enMalayalam || '') + ' ' + (ml.secondTithi.mlMalayalam || '')) : '')
        ).toLowerCase();
        if (label.indexOf('amavas') > -1 || label.indexOf('അമാവാസി') > -1) {
            return 'new';
        }
        if (label.indexOf('pournami') > -1 || label.indexOf('paurnami') > -1 ||
            label.indexOf('പൗർണമി') > -1 || label.indexOf('പൌർണമി') > -1 || label.indexOf('പൗർണ്ണമി') > -1) {
            return 'full';
        }
        return null;
    }

    trackByMonth = (_index: number, m: PcvMonth) => m.key;

    // ---- scrolling --------------------------------------------------------

    private bindScrollListener(): void {
        const host = this.scrollHost && this.scrollHost.nativeElement;
        if (!host) {
            return;
        }
        // Outside the zone: scrolling must not run app-wide change detection.
        // Re-enter only to append a month or when the sticky label changes.
        this.zone.runOutsideAngular(() => {
            const onScroll = () => {
                // ALL prechecks run outside the zone — re-entering (and marking
                // the view dirty) on a no-op scroll event loops change
                // detection against layout-induced scroll events (NG0103)
                const nearBottom = host.scrollTop + host.clientHeight
                    > host.scrollHeight - Math.max(900, host.clientHeight);
                // build ahead a full viewport early — arriving at an unbuilt
                // month mid-gesture is what scroll jank feels like
                if (nearBottom && this.monthBlocks.length && !this.appending) {
                    this.zone.run(() => this.loadMore());
                }
                // after the first "Earlier month" click, the top edge keeps
                // loading previous months (continuous upward scroll)
                if (this.earlierUnlocked && host.scrollTop < 400 && !this.appending && this.canLoadEarlier) {
                    this.zone.run(() => this.loadEarlierAuto());
                }
                if (this.tip || this.headEdit) {
                    // both float FIXED beside their anchors — stale positions
                    // (and half-cropped panels) must never survive a scroll
                    this.zone.run(() => {
                        this.hideTip();
                        this.headEdit = null;
                    });
                }
                this.updateVisibleLabel();
            };
            host.addEventListener('scroll', onScroll, { passive: true });
            this.removeScrollListener = () => host.removeEventListener('scroll', onScroll);
        });
    }

    private updateVisibleLabel(): void {
        const host = this.scrollHost && this.scrollHost.nativeElement;
        if (!host) {
            return;
        }
        const sections = host.querySelectorAll<HTMLElement>('.pcv-month');
        let label = '';
        let key: number | null = null;
        for (let i = 0; i < sections.length; i++) {
            const s = sections[i];
            if (s.offsetTop + s.offsetHeight > host.scrollTop + 8) {
                label = s.getAttribute('data-ml') || '';
                key = +(s.getAttribute('data-key') || 0) || null;
                break;
            }
        }
        if ((label !== this.visibleMlLabel || key !== this.visibleKey) && !this.destroyed) {
            this.zone.run(() => {
                this.visibleMlLabel = label;
                this.visibleKey = key;
                this.cdr.markForCheck();
            });
        }
    }

    /** The month block under the sticky header (toolbar month/year source). */
    get visibleBlock(): PcvMonth | null {
        if (!this.monthBlocks.length) {
            return null;
        }
        if (this.visibleKey !== null) {
            const found = this.monthBlocks.find(b => b.key === this.visibleKey);
            if (found) {
                return found;
            }
        }
        return this.monthBlocks[0];
    }

    private get nowMonthKey(): number {
        const nowP = istParts(bookingNow());
        return nowP.year * 12 + nowP.month;
    }

    /** "Current month" return button — only when scrolled away from it. */
    get showTodayButton(): boolean {
        return this.visibleKey !== null && this.visibleKey !== this.nowMonthKey;
    }

    goToCurrentMonth(): Promise<void> {
        const key = this.nowMonthKey;
        return this.jumpToMonth(Math.floor(key / 12), key % 12);
    }

    private scrollToMonthKey(key: number): void {
        const host = this.scrollHost && this.scrollHost.nativeElement;
        if (!host) {
            return;
        }
        let section = host.querySelector<HTMLElement>('.pcv-month[data-key="' + key + '"]');
        if (!section) {
            // filtered list: a month with no matching rows collapses — land on
            // the nearest following month, else the last one
            const sections = Array.from(host.querySelectorAll<HTMLElement>('.pcv-month'));
            section = sections.find(s => +(s.getAttribute('data-key') || 0) >= key)
                || sections[sections.length - 1] || null;
        }
        if (section) {
            let top = section.offsetTop;
            // list view (mobile): land on the anchor DAY's row, tucked just
            // under the sticky month bar (owner: open at the current day)
            if (this.listMode && this.pendingScrollDayKey) {
                const row = section.querySelector<HTMLElement>(
                    '.pcv-row[data-day="' + this.pendingScrollDayKey + '"]');
                if (row) {
                    top = Math.max(section.offsetTop, row.offsetTop - 42);
                }
            }
            this.pendingScrollDayKey = null;
            // instant landing — an animated multi-month glide here fights the
            // month snap and reads as "scroll not working"
            if (typeof host.scrollTo === 'function') {
                host.scrollTo({ top, behavior: 'auto' });
            } else {
                host.scrollTop = top;
            }
        }
        this.updateVisibleLabel();
    }

    /** The IST-style day key of a cell (row anchor + selection identity). */
    dayKeyOf(day: any): number {
        return day.year * 10000 + (day.month + 1) * 100 + day.date;
    }

    // ---- selection --------------------------------------------------------

    onDayClick(day: any): void {
        this.choosedate(day, null);
    }

    override choosedate(day, _weeks?, _isKeyBoard = false) {
        // isPartial === false marks a fully-blocked date (red cell) — e.g. a
        // limited pooja whose slots for that day are taken. It must not be
        // selectable; blocked = before the booking-day minimum. R19: a WARN-ONLY
        // pooja's red days are `overbookable` and stay selectable (overbooking is
        // permitted with the R18 confirmation on add).
        if (!day || !day.date || day.blocked || (day.isPartial === false && !day.overbookable)) {
            return;
        }
        if (this.multiMode) {
            this.toggleMultiDay(day);
            return;
        }
        const selectedDay = {
            enyear: day.year,
            date: day.date,
            enmonth: day.month + 1,
        };
        this.markSingleSelection(day);
        const dateStr = `${selectedDay.date < 10 ? '0' + selectedDay.date : selectedDay.date}/${selectedDay.enmonth < 10 ? '0' + selectedDay.enmonth : selectedDay.enmonth}/${selectedDay.enyear}`;
        this.dateChanged.emit(dateStr);

        // Support standalone modal usage
        if (this.activeModal) {
            this.activeModal.close(dateStr);
            if (this.punchangamModalService) {
                this.punchangamModalService.panchangModalEvent.next({ result: 'close', date: dateStr });
            }
        }
    }

    private markSingleSelection(day: any): void {
        this.monthBlocks.forEach(m => m.weeks.forEach(week => week.forEach((d: any) => {
            if (d && d.isSelected) {
                d.isSelected = false;
            }
        })));
        day.isSelected = true;
    }

    private toggleMultiDay(day: any): void {
        const key = day.year * 10000 + (day.month + 1) * 100 + day.date;
        if (this.multiSelected.has(key)) {
            this.multiSelected.delete(key);
            day.isMultiSelected = false;
        } else {
            this.multiSelected.set(key, { year: day.year, month: day.month, date: day.date });
            day.isMultiSelected = true;
        }
    }

    get multiCount(): number {
        return this.multiSelected.size;
    }

    /** The "Multiple Date" toolbar button: toggles in-calendar multi-select
     *  (tap days, then the full-width Submit at the bottom). Exclusive with
     *  the filter view — turning it on closes/clears the filter panel. */
    toggleMultipleDate(): void {
        if (this.hideMultipleDate) {
            return;
        }
        this.multiMode = !this.multiMode;
        if (this.multiMode && this.filterOpen) {
            this.filterOpen = false;
            this.clearFilters();
            this.multiMode = true; // clearFilters exits multi — this is a fresh plain multi session
        }
        if (!this.multiMode) {
            this.unselectAll();
        }
    }

    /** Full-width Submit of multi-select mode — emits the tapped days as the
     *  SAME payload the continuous-booking modal emits (IST-midnight epochs,
     *  ascending) through the same panchangModalEvent channel, so every
     *  caller handles it unchanged. */
    submitMultiDates(): void {
        if (!this.multiSelected.size) {
            return;
        }
        const dates = Array.from(this.multiSelected.values())
            .map(d => istEpoch(d.year, d.month, d.date))
            .sort((a, b) => a - b);
        this.closePanchang.emit();
        if (this.activeModal) {
            this.activeModal.close();
        }
        if (this.punchangamModalService) {
            this.punchangamModalService.panchangModalEvent.next({ result: 'continous', date: dates });
        }
    }

    /** Top-right close (and Esc): cancel without a date. close(undefined) —
     *  not dismiss — so single-arg .result.then(...) callers see a falsy
     *  result instead of an unhandled rejection (legacy semantics). */
    cancel(): void {
        this.closePanchang.emit();
        if (this.activeModal) {
            this.activeModal.close();
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.headEdit) {
            this.headEdit = null;
            return;
        }
        this.cancel();
    }

    // Continuous/multiple-date booking is not allowed for limited poojas
    // (propertyId 1/2/20; 1 and 2 also carry isLimitPooja). The pooja comes
    // either from the @Input (online modal usage) or the host booking component
    // (desktop embedding), so check both.
    get hideMultipleDate(): boolean {
        const pooja: any = this.pooja || (this.poojaBookingComp && (this.poojaBookingComp as any).selectedPooja);
        return !!pooja && ([1, 2, 20].indexOf(pooja.propertyId) > -1 || !!pooja.isLimitPooja);
    }

    // ---- in-panel continuous-booking filter (owner round 5) ----------------
    // Mobile: pick ONE dimension (star/thithi/weekday/month) and its value —
    // only matching day rows stay visible. Desktop: all dropdowns at once
    // (AND), like the continuous modal's multi filter, but in this panel.
    // The legacy Continuous Booking modal stays reachable ("Advanced") for
    // pradosham / available-day-count / from-to ranges.

    /** Day rows instead of the month grid: phones always; anywhere once a
     *  filter is active (a filtered month grid would be mostly holes). */
    get listMode(): boolean {
        return this.isMobile || this.filtersActive;
    }

    /** Any star/thithi/weekday/month/ml-month/pradosham dimension picked. */
    get anyDimensionFilter(): boolean {
        return !!(this.filters.star || this.filters.thithi || this.filters.week
            || this.filters.month || this.filters.mlMonth || this.filters.pradosham);
    }

    get filtersActive(): boolean {
        return this.anyDimensionFilter || !!(this.filterRange && this.filterRangeTouched);
    }

    // MEMOIZED per language: these feed *ngFor inside [ngModel] selects — a
    // getter minting fresh option objects every change-detection pass makes
    // the select rebuild its <option>s each pass, whose value re-sync marks
    // the view dirty again -> NG0103 infinite change detection. (Same rule as
    // the continuous modal's dayjs getters: never bind a fresh-object getter.)
    private optionCacheLang: string | null = null;
    private starOptionsCache: { value: string; label: string }[] = [];
    private thithiOptionsCache: { value: string; label: string }[] = [];
    private weekOptionsCache: { value: string; label: string }[] = [];
    private monthOptionsCache: { value: string; label: string }[] = [];
    private mlMonthOptionsCache: { value: string; label: string }[] = [];

    private ensureFilterOptions(): void {
        const lang = this.lang || '';
        if (this.optionCacheLang === lang) {
            return;
        }
        this.optionCacheLang = lang;
        const ml = lang === 'malayalam';
        this.starOptionsCache = NAKSATRAS.filter((n: any) => n.en !== 'ALL')
            .map((n: any) => ({ value: ml ? n.ml : n.en, label: ml ? n.ml : n.en }));
        this.thithiOptionsCache = TITHI.map((t: any) => ({ value: ml ? t.ml : t.en, label: ml ? t.ml : t.en }));
        this.weekOptionsCache = WEEKDAYS.map((w: any, i: number) => ({ value: String(i), label: ml ? w.ml : w.en }));
        // English months + the legacy "ENGLISH 1" (= 1st of every EN month)
        this.monthOptionsCache = [{ value: 'E1', label: 'ENGLISH 1' },
        ...EN_MONTHS.map((m: any, i: number) => ({ value: String(i), label: (ml ? m.ml : m.en).trim() }))];
        // Malayalam months + "മലയാളം 1" (= 1st of every Malayalam month)
        this.mlMonthOptionsCache = [{ value: 'M1', label: ml ? 'മലയാളം 1' : 'MALAYALAM 1' },
        ...ML_MONTHS.map((m: any, i: number) => ({ value: 'ml' + i, label: ml ? m.ml : m.en }))];
    }

    get starOptions(): { value: string; label: string }[] {
        this.ensureFilterOptions();
        return this.starOptionsCache;
    }

    get thithiOptions(): { value: string; label: string }[] {
        this.ensureFilterOptions();
        return this.thithiOptionsCache;
    }

    get weekOptions(): { value: string; label: string }[] {
        this.ensureFilterOptions();
        return this.weekOptionsCache;
    }

    get monthOptions(): { value: string; label: string }[] {
        this.ensureFilterOptions();
        return this.monthOptionsCache;
    }

    get mlMonthOptions(): { value: string; label: string }[] {
        this.ensureFilterOptions();
        return this.mlMonthOptionsCache;
    }

    // ONE grouped Week dropdown (owner round 13): optgroup per weekday whose
    // options carry the occurrence — replaces the Week day + occurrence
    // sub-select pair, freeing the bar slot for the Advanced button. Memoized
    // per language (fresh-object getters under [ngModel] selects loop CD,
    // NG0103).
    private weekComboCacheLang: string | null = null;
    private weekComboCache: { label: string; options: { value: string; label: string }[] }[] = [];

    /** Per weekday: "<day>" (every occurrence), then the legacy modal's week
     *  sub-filter rows — m: 1st..Last <day> (Malayalam-month-relative) and
     *  e: 1st..Last <day> (English month). Values '<i>' / '<i>:m1'..'<i>:e5'. */
    get weekComboGroups(): { label: string; options: { value: string; label: string }[] }[] {
        const lang = this.lang || '';
        if (this.weekComboCacheLang === lang) {
            return this.weekComboCache;
        }
        this.weekComboCacheLang = lang;
        const ml = lang === 'malayalam';
        const ordinals = ['1st', '2nd', '3rd', '4th', 'Last'];
        this.weekComboCache = WEEKDAYS.map((w: any, i: number) => {
            const name = ml ? w.ml : w.en;
            return {
                label: name,
                options: [
                    { value: String(i), label: name },
                    ...ordinals.map((o, n) => ({ value: i + ':m' + (n + 1), label: 'm: ' + o + ' ' + name })),
                    ...ordinals.map((o, n) => ({ value: i + ':e' + (n + 1), label: 'e: ' + o + ' ' + name })),
                ],
            };
        });
        return this.weekComboCache;
    }

    /** The combined select's current value: '' | '<weekday>' | '<weekday>:<occ>'. */
    get weekComboValue(): string {
        if (this.filters.week === '') {
            return '';
        }
        return this.weekOption ? this.filters.week + ':' + this.weekOption : this.filters.week;
    }

    toggleFilterPanel(): void {
        if (this.hideMultipleDate) {
            return;
        }
        this.filterOpen = !this.filterOpen;
        if (this.filterOpen) {
            // exclusive with Multiple Date mode (owner round 7): opening the
            // filter drops any plain multi-select session; the filter drives
            // its own selection once a dimension is picked
            this.multiMode = false;
            this.unselectAll();
            this.filterDimension = '';
            this.filters = { star: '', thithi: '', week: '', month: '', mlMonth: '', pradosham: false };
            this.weekOption = '';
            this.setDefaultFilterRange();
        } else {
            this.clearFilters();
        }
    }

    /** The panel's default scope: the CURRENT IST DAY through the same day
     *  next year (owner round 9 — exactly the advanced continuous window's
     *  from/to defaults, e.g. 17-Jul-2026 → 17-Jul-2027; the picker shows the
     *  months, "Jul 2026 to Jul 2027") — dimension filters select over it. */
    private setDefaultFilterRange(): void {
        const p = istParts(bookingNow());
        const start = dayjs(new Date(p.year, p.month, p.day));
        const end = dayjs(new Date(p.year + 1, p.month, p.day));
        this.filterRangeStartDj = start;
        this.filterRangeEndDj = end;
        this.filterRange = {
            startKey: start.year() * 10000 + (start.month() + 1) * 100 + start.date(),
            endKey: end.year() * 10000 + (end.month() + 1) * 100 + end.date(),
        };
        this.filterRangeTouched = false;
    }

    /** Mobile single-filter flow: choosing a dimension clears the others so
     *  exactly one value dropdown drives the list (and the previous
     *  dimension's auto-selection goes with it). */
    onFilterDimensionChange(dimension: any): void {
        this.filterDimension = dimension || '';
        this.filters = { star: '', thithi: '', week: '', month: '', mlMonth: '', pradosham: false };
        this.weekOption = '';
        this.unselectAll();
        if (!this.filtersActive) {
            this.multiMode = false;
        }
    }

    async onFilterValueChange(dimension: 'star' | 'thithi' | 'week' | 'month' | 'mlMonth', value: any): Promise<void> {
        this.filters = { ...this.filters, [dimension]: value || '' };
        if (dimension === 'week' && !value) {
            this.weekOption = ''; // the occurrence sub-filter goes with its weekday
        }
        this.cdr.markForCheck();
        if (this.anyDimensionFilter) {
            // filtering exists to pick MANY days (continuous booking)
            if (!this.multiMode && !this.hideMultipleDate) {
                this.multiMode = true;
            }
            await this.applyFilterSelection();
        } else {
            // the selection was filter-driven — dropping the last dimension
            // drops it too
            this.unselectAll();
            if (!this.filtersActive) {
                this.multiMode = false;
            }
        }
    }

    /** The combined Week dropdown's change: '4:e5' = Last <Thu> of every
     *  English month, '4' = every <Thu>, '' clears the dimension — one
     *  handler sets both the weekday and its occurrence, then re-selects
     *  over the scope like any other dimension change. */
    async onWeekComboChange(value: any): Promise<void> {
        const v = String(value == null ? '' : value);
        const sep = v.indexOf(':');
        this.weekOption = sep >= 0 ? v.slice(sep + 1) : '';
        await this.onFilterValueChange('week', sep >= 0 ? v.slice(0, sep) : v);
    }

    /** Period picked with the month-granularity app-date-picker (dayjs
     *  {start,end} or null) — scopes the filter to that window (e.g. a star
     *  across 3 years) and extends the built months to cover it. null
     *  (cleared/cancelled pick) falls back to the 1-year default while the
     *  panel is open. */
    async onFilterRangeChange(value: any): Promise<void> {
        if (value && value.start && value.end) {
            // Day-anchor the picked MONTHS to today (owner round 10): the
            // range is "N years/months from now", so the start never reaches
            // before the current day (picking the current month means "from
            // today", not "from the 1st") and the end lands on today's
            // day-of-month in the picked month (17-Jul-2030, not 31-Jul-2030).
            const today = istParts(bookingNow());
            const todayKey = today.year * 10000 + (today.month + 1) * 100 + today.day;
            let s = value.start;
            let e = value.end;
            const sKey = s.year() * 10000 + (s.month() + 1) * 100 + s.date();
            if (sKey < todayKey) {
                s = dayjs(new Date(today.year, today.month, today.day));
            }
            e = e.date(Math.min(today.day, e.daysInMonth()));
            if (e.isBefore(s, 'day')) {
                // a picked period fully in the past collapses to today after
                // the start clamp — never leave an inverted (empty) scope
                e = s;
            }
            this.filterRangeStartDj = s;
            this.filterRangeEndDj = e;
            this.filterRange = {
                startKey: s.year() * 10000 + (s.month() + 1) * 100 + s.date(),
                endKey: e.year() * 10000 + (e.month() + 1) * 100 + e.date(),
            };
            this.filterRangeTouched = true;
            if (!this.multiMode && !this.hideMultipleDate) {
                this.multiMode = true;
            }
        } else if (this.filterOpen) {
            this.setDefaultFilterRange();
        } else {
            this.filterRange = null;
            this.filterRangeStartDj = null;
            this.filterRangeEndDj = null;
            this.filterRangeTouched = false;
        }
        this.cdr.markForCheck();
        if (this.anyDimensionFilter) {
            // the window changed under an active filter — reselect over it
            await this.applyFilterSelection();
        } else if (this.filterRange && this.filterRangeTouched) {
            await this.extendWindowForFilter();
        }
    }

    clearFilters(): void {
        this.filterDimension = '';
        this.filters = { star: '', thithi: '', week: '', month: '', mlMonth: '', pradosham: false };
        this.weekOption = '';
        if (this.filterOpen) {
            this.setDefaultFilterRange();
        } else {
            this.filterRange = null;
            this.filterRangeStartDj = null;
            this.filterRangeEndDj = null;
            this.filterRangeTouched = false;
        }
        // the filter's selection goes with it
        this.unselectAll();
        this.multiMode = false;
        this.cdr.markForCheck();
    }

    /** A star like Aayilyam hits ~once a month — a useful filtered list needs
     *  a year of months (or the picked period, capped at 5 years: a 4-year
     *  pick used to stop selecting at month 36). Builds ahead in the
     *  background (macrotask-hopped); scrolling extends further as usual.
     *  `minTargetKey` additionally covers a jumped-to month. */
    private async extendWindowForFilter(minTargetKey?: number): Promise<void> {
        const seq = this.initSeq;
        const targetMonths = () => {
            if (!this.monthBlocks.length) {
                return 0;
            }
            const first = this.monthBlocks[0];
            let months = 12;
            if (this.filterRange) {
                const endKey = Math.floor(this.filterRange.endKey / 10000) * 12
                    + (Math.floor(this.filterRange.endKey / 100) % 100) - 1;
                months = Math.max(1, endKey - first.key + 1);
            }
            if (minTargetKey) {
                months = Math.max(months, minTargetKey - first.key + 1);
            }
            return Math.min(61, months);
        };
        while (seq === this.initSeq && this.monthBlocks.length < targetMonths()) {
            const before = this.monthBlocks.length;
            await this.loadMore();
            if (this.monthBlocks.length === before) {
                break; // ceiling reached or a concurrent append is running
            }
            await new Promise(resolve => setTimeout(resolve));
        }
    }

    /** Occurrence sub-filter match ('e'nglish / 'm'alayalam month-relative;
     *  1..4 = the nth weekday of the month, 5 = the LAST one). The day has
     *  already matched the weekday itself. */
    private matchesWeekOccurrence(day: any): boolean {
        const type = this.weekOption.charAt(0);
        const n = +this.weekOption.slice(1);
        if (type === 'e') {
            if (n === 5) {
                // last <weekday> of the English month: no same weekday 7 days on
                return day.date + 7 > new Date(day.year, day.month + 1, 0).getDate();
            }
            return Math.ceil(day.date / 7) === n;
        }
        const mlDate = +((day.mlDetails && day.mlDetails.mlDate) || 0);
        if (n === 5) {
            // last <weekday> of the MASA: the same weekday 7 days later falls
            // in a different Malayalam month (masa lengths vary 29–32, so this
            // is checked against the real neighbouring cell; a missing
            // neighbour at the window edge counts as last)
            const next = this.dayPlusSeven(day);
            return !next || !next.mlDetails || !day.mlDetails
                || next.mlDetails.masaName !== day.mlDetails.masaName
                || next.mlDetails.mlyear !== day.mlDetails.mlyear;
        }
        return Math.ceil(mlDate / 7) === n;
    }

    /** The cell exactly 7 days after `day`, wherever its month block lives. */
    private dayPlusSeven(day: any): any | null {
        const d = new Date(day.year, day.month, day.date + 7);
        const block = this.monthBlocks.find(b => b.key === d.getFullYear() * 12 + d.getMonth());
        return block ? block.days.find((x: any) => x && x.date === d.getDate()) || null : null;
    }

    dayMatchesFilters(day: any): boolean {
        if (!day || !day.date) {
            return false;
        }
        if (this.filterRange) {
            const key = this.dayKeyOf(day);
            if (key < this.filterRange.startKey || key > this.filterRange.endKey) {
                return false;
            }
        }
        const f = this.filters;
        if (f.week !== '' && day.day !== +f.week) {
            return false;
        }
        if (f.week !== '' && this.weekOption && !this.matchesWeekOccurrence(day)) {
            return false;
        }
        if (f.month !== '') {
            if (f.month === 'E1') {
                // the legacy "ENGLISH 1": the 1st of every English month
                if (day.date !== 1) {
                    return false;
                }
            } else if (day.month !== +f.month) {
                return false;
            }
        }
        if (f.mlMonth) {
            if (f.mlMonth === 'M1') {
                // "മലയാളം 1": the 1st of every Malayalam month
                if (+((day.mlDetails && day.mlDetails.mlDate) || 0) !== 1) {
                    return false;
                }
            } else {
                const m: any = ML_MONTHS[+f.mlMonth.slice(2)];
                const masa = ((day.mlDetails && day.mlDetails.masaName) || '') + ' '
                    + ((day.mlDetails && day.mlDetails.mlMasaName) || '');
                if (!m || (masa.indexOf(m.en) === -1 && masa.indexOf(m.ml) === -1)) {
                    return false;
                }
            }
        }
        if (f.star) {
            const names = (day.mlDetails.naksatra.enMalayalam || '') + ' ' + (day.mlDetails.naksatra.mlMalayalam || '')
                + (day.mlDetails.secondStar
                    ? ' ' + (day.mlDetails.secondStar.enMalayalam || '') + ' ' + (day.mlDetails.secondStar.mlMalayalam || '') : '');
            if (names.indexOf(f.star) === -1) {
                return false;
            }
        }
        if (f.thithi) {
            const names = (day.mlDetails.tithi.enMalayalam || '') + ' ' + (day.mlDetails.tithi.mlMalayalam || '')
                + (day.mlDetails.secondTithi
                    ? ' ' + (day.mlDetails.secondTithi.enMalayalam || '') + ' ' + (day.mlDetails.secondTithi.mlMalayalam || '') : '');
            if (names.indexOf(f.thithi) === -1) {
                return false;
            }
        }
        if (f.pradosham && !this.pradoshamDayKeys().has(this.dayKeyOf(day))) {
            return false;
        }
        return true;
    }

    // ---- Pradosham day lookup (owner round 14) ------------------------------
    // Same source of truth as the legacy Continuous Booking modal's Pradosham
    // checkbox: panchang.json rows carrying a truthy `pradosham` field (the
    // frozen dump includes it). Read from `fullDataLoaded` — the BehaviorSubject
    // RETAINS the loaded JSON array — and NEVER from ps.panchangFor50Year:
    // continousBookingDetails() (counter star/packaged-pooja suggestion flows)
    // REPLACES that table with computed rows carrying no pradosham flag, which
    // made the checkbox dead on the desktop while the web portal (JSON loaded
    // first) worked. Memoized by array identity; days outside the JSON window
    // carry no flag — the SAME limitation the legacy filter has, faithfully
    // kept rather than re-deriving pradosham here.
    private pradoshamKeysCache: Set<number> | null = null;
    private pradoshamKeysSource: any[] | null = null;

    private pradoshamDayKeys(): Set<number> {
        const rows: any[] = this.ps.fullDataLoaded.getValue() || [];
        if (this.pradoshamKeysCache && this.pradoshamKeysSource === rows) {
            return this.pradoshamKeysCache;
        }
        this.pradoshamKeysSource = rows;
        this.pradoshamKeysCache = new Set<number>(
            rows.filter((r: any) => !!r.pradosham).map((r: any) => istDayKey(r.date)));
        return this.pradoshamKeysCache;
    }

    /** The Pradosham checkbox: filters to pradosham days like any other
     *  dimension change. The 12.5 MB panchang table loads lazily — if the
     *  JSON capture is not here yet, trigger the shared load and re-run the
     *  selection when it lands (destroyed-guarded). */
    async onPradoshamChange(checked: boolean): Promise<void> {
        this.filters = { ...this.filters, pradosham: !!checked };
        if (checked && !this.ps.fullDataLoaded.getValue()) {
            this.ps.ensureLoaded();
            this.ps.fullDataLoaded.pipe(first(v => !!v)).subscribe(() => {
                if (!this.destroyed && this.filters.pradosham) {
                    this.cdr.markForCheck();
                    if (this.anyDimensionFilter) {
                        this.applyFilterSelection();
                    }
                }
            });
        }
        this.cdr.markForCheck();
        if (this.anyDimensionFilter) {
            if (!this.multiMode && !this.hideMultipleDate) {
                this.multiMode = true;
            }
            await this.applyFilterSelection();
        } else {
            this.unselectAll();
            if (!this.filtersActive) {
                this.multiMode = false;
            }
        }
    }

    /** The rows a month contributes to the list view (all days on mobile,
     *  matching days when filtering). */
    visibleDays(m: PcvMonth): any[] {
        return this.filtersActive ? m.days.filter(d => this.dayMatchesFilters(d)) : m.days;
    }

    /** Choosing a dimension filter SELECTS every matching day in the scope by
     *  default (owner round 7) — after the window has grown to cover it. A
     *  filter change replaces the previous filter's selection. */
    private async applyFilterSelection(): Promise<void> {
        const seq = this.initSeq;
        await this.extendWindowForFilter();
        if (seq !== this.initSeq || this.destroyed || !this.anyDimensionFilter) {
            return;
        }
        this.unselectAll();
        this.selectAllVisible();
    }

    /** The single footer button of the filter view: Unselect all ⇄ Select all
     *  (replaces the two top-bar buttons). */
    toggleSelectAll(): void {
        if (this.multiCount) {
            this.unselectAll();
        } else {
            this.selectAllVisible();
        }
    }

    /** "Select all" over the filtered rows (selectable days only). */
    selectAllVisible(): void {
        if (!this.multiMode) {
            return;
        }
        this.monthBlocks.forEach(m => this.visibleDays(m).forEach(day => {
            if (!day.blocked && day.isPartial !== false && !day.isMultiSelected) {
                this.toggleMultiDay(day);
            }
        }));
        this.cdr.markForCheck();
    }

    /** Drops every ticked day (filter bar + the multi footer). */
    unselectAll(): void {
        this.multiSelected.clear();
        this.monthBlocks.forEach(m => m.weeks.forEach(week => week.forEach((d: any) => {
            if (d && d.isMultiSelected) {
                d.isMultiSelected = false;
            }
        })));
        this.cdr.markForCheck();
    }

    /** The fa-filter button of multi-select mode: the legacy Multiple Date
     *  flow — closes the calendar and opens the Continuous Booking modal
     *  (star/thithi/week/pradosham suggestion filters); its result is routed
     *  back through panchangModalEvent exactly as before. */
    onMultipleDateClick() {
        if (this.hideMultipleDate) {
            return;
        }
        this.closePanchang.emit();
        if (this.activeModal) {

            this.activeModal.close();
        }
        // Booking-day policy: the modal's from-date starts at the context
        // minimum (the old modeDate+1 equalled this under legacy values).
        // PAST (null minimum) starts the recurring range from today.
        const minBookable = contextMinBookingDate();

        this.continuosBookingModalService.
            openModalForContinuosBooking(ContinuosBookingModalComponent as Component, {
                startDate: minBookable
                    || new Date(istStartOfDay(bookingNow())),
                keyboard: this.keyboard
            })
            .result.then((result) => {
                if (result) {
                    this.punchangamModalService.panchangModalEvent.next({ result: 'continous', date: result })
                } else if (result === false) {
                    this.punchangamModalService.panchangModalEvent.next({ result: 'cancel' })

                }

            }, () => {

            });
    }
}
